import React, { useState, useEffect } from "react";
import MainLayout from "./MainLayout";
import Loader from "../utils/Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Box,
  Button,
  TableSortLabel,
  Grid,
  InputAdornment,
  Switch,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchLogDetail } from "../../redux/slices/logSlice";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { CalendarMonth, CalendarToday, Search } from "@mui/icons-material";
import TableChartIcon from "@mui/icons-material/TableChart";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import DateRangeFilter from "../utils/DateRangeFilter";
import MultiSelectWithCheckbox from "../utils/MultiSelectWithCheckbox";
import RoleSelect from "../utils/RoleSelect";
import { fetchUserOption } from "../../redux/slices/clientSlice";
import { ClickableTableLink } from "./ClickableTableLink";
import { useLocation } from "react-router-dom";
import MonthFY from "../MonthFY";
import YearFY from "../YearFY";
import FilterBar2 from "./admin_dashboard/FilterBar2";

const FY_YEAR_OPTIONS = [
  { label: "2026-2027", value: "2026" },
  { label: "2025-2026", value: "2025" },
  { label: "2024-2025", value: "2024" },
  { label: "2023-2024", value: "2023" },
];

const LogDetails = () => {
  const { state: navState } = useLocation();

  const dispatch = useDispatch();
  const { logDetail, userOptions, count, status } = useSelector(state => state.logs);
  const [searchQuery, setSearchQuery] = useState("");
  const [month, setMonth] = useState(() => {
    if (navState && navState.months) return navState.months;

    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    return fullFYMonths.slice(0, currentMonthIndex + 1);
  });
const [year, setYear] = useState(() => {
  if (navState && navState.years?.length > 0) {
    const targetYrs = navState.years.map(String);
    return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
  }
  const currentYrStr = String(new Date().getFullYear());
  return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
});
  const counts = count || 0
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    sort_by: "created_at",
    order: "desc",
  });
  const [roleId, setRoleId] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const { userOption } = useSelector((state) => state.client);
  const { roleOpt } = useSelector((state) => state.adminOption);
  const [isOnlineOnly, setIsOnlineOnly] = useState(1);

  const hasValidFilters = Boolean(year?.length > 0 && month?.length > 0);

  useEffect(() => {
    dispatch(fetchUserOption());
  }, []);

  useEffect(() => {
    if (!hasValidFilters) {
      return;
    }
    dispatch(fetchLogDetail({
      search: searchQuery, page, rowsPerPage,
      user: selectedUsers?.map((u) => u.value),
      month: month,
      year: year?.map((y) => y.value),
      roleId: roleId?.map((u) => u.value),
      sort_by: sortBy.sort_by,
      sort_order: sortBy.order,
      onlineOnly: isOnlineOnly
    }))
  }, [searchQuery, page, rowsPerPage, month, year, sortBy, roleId, isOnlineOnly, selectedUsers, hasValidFilters])

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadExcel = () => {
    const filteredDataForExcel = logDetail?.map((item, index) => ({
      "Sr. No": index + 1,
      "User Type": item.user_type,
      "EMP Code/GST No.": item.emp_code,
      Name: item.name,
      Role: item.role_name,
      Email: item.email,
      Phone: item.phone,
      Date: item ? new Date(item.last_login_time).toLocaleDateString("en-GB") : "",
      Time: new Date(item.last_login_time).toLocaleString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredDataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(blob, "log_list.xlsx");
  };

  const userOpt = userOption
    ? userOption.map((item) => ({
      value: item.id,
      label: `${item.name} ${item.last_name}`,
    }))
    : [];


  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: column, order: "asc" };
    });
  };

const filterConfigs = [
  { id: "user", label: "User", options: userOpt || [], selected: selectedUsers },
  { id: "role", label: "Role", options: roleOpt || [], selected: roleId },
  { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
];

const handleMonthChange = (newMonths) => {
  setMonth(newMonths);
  if (newMonths?.length > 0 && year?.length === 0) {
    const currentYrStr = String(new Date().getFullYear());
    setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
  }
  setPage(0);
};

const handleFilterChange = (filterId, nextSelected) => {
  switch (filterId) {
    case "user": setSelectedUsers(nextSelected); break;
    case "role": setRoleId(nextSelected); break;
    case "year": 
      setYear(nextSelected); 
      if (nextSelected.length === 0) {
        setMonth([]);
      } else if (month?.length === 0) {
        const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const currentCalendarMonth = new Date().getMonth() + 1;
        const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
        setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
      }
      break;
    default: break;
  }
  setPage(0);
};

const handleClearFilters = () => {
  setSelectedUsers([]);
  setRoleId([]);
  setSearchQuery("");
  setIsOnlineOnly(1);
  setSortBy({ sort_by: "created_at", order: "desc" });
  setPage(0);

  const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
  const currentCalendarMonth = new Date().getMonth() + 1;
  const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
  setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));

  const currentYrStr = String(new Date().getFullYear());
  setYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
};


  return (
    <MainLayout title={'Log History'}>
      <Box sx={{ p: 0 }}>
<FilterBar2
    filters={filterConfigs}
    onFilterChange={handleFilterChange}
    selectedMonth={month}
    setSelectedMonth={handleMonthChange}
    searchTerm={searchQuery}
    setSearchTerm={(val) => { setSearchQuery(val); setPage(0); }}
    onClearFilters={handleClearFilters}
    actions={
      <div className="flex items-center gap-3">
        {/* Activity In Month Switch Widget */}
        <div className="flex items-center bg-white border border-[#e0e6ed] px-3 h-8 rounded-xl shadow-sm transition-all hover:border-slate-300">
          <div className="relative flex items-center justify-center mr-2">
            <div
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                isOnlineOnly === 1 ? "bg-emerald-500 shadow-[0_0_8px_#10b981]" : "bg-slate-300"
              }`}
            />
            {isOnlineOnly === 1 && (
              <div className="absolute w-2 h-2 rounded-full bg-emerald-500 animate-ping opacity-40" />
            )}
          </div>

          <div className="flex flex-col min-w-[65px]">
            <span className="text-[7px] text-slate-400 font-extrabold uppercase tracking-widest leading-none">
              Activity
            </span>
            <span
              className={`text-[9px] font-bold uppercase leading-tight transition-colors duration-300 ${
                isOnlineOnly === 1 ? "text-emerald-700" : "text-slate-500"
              }`}
            >
              {isOnlineOnly === 1 ? "Online" : "Offline"}
            </span>
          </div>

          <Switch
            size="small"
            checked={isOnlineOnly === 1}
            onChange={(e) => setIsOnlineOnly(e.target.checked ? 1 : 0)}
            sx={{
              ml: 0.5,
              padding: 0.5,
              "& .MuiSwitch-switchBase": {
                padding: 0.8,
                "&.Mui-checked": {
                  color: "#fff",
                  transform: "translateX(14px)",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#10b981",
                    opacity: 1,
                  },
                },
              },
              "& .MuiSwitch-track": {
                borderRadius: 10,
                backgroundColor: "#e2e8f0",
                opacity: 1,
              },
              "& .MuiSwitch-thumb": {
                width: 12,
                height: 12,
              },
            }}
          />
        </div>

        {/* Excel Button */}
        <Button
          variant="contained"
          startIcon={<TableChartIcon />}
          onClick={handleDownloadExcel}
          sx={{
            textTransform: "none",
            bgcolor: "#2e7d32",
    py: "2px",
    px: "14px",
    borderRadius: 12,
            fontSize: "12px",
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: "#1b5e20" },
          }}
        >
          Excel
        </Button>
      </div>
    }
  />
        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ background: "#04121f", color: "white" }}>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  Sr. No
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  User Type
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  EMP Code/GST No.
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "name"}
                    direction={
                      sortBy.sort_by === "name" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("name")}
                    hideSortIcon={false} // <-- keep icon visible even when inactive
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1, // <-- don't fade when inactive
                        color: "white !important",
                      },
                    }}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "role_id"}
                    direction={
                      sortBy.sort_by === "role_id" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("role_id")}
                    hideSortIcon={false}
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1,
                        color: "white !important",
                      },
                    }}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  Email
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  Phone
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortBy.sort_by === "date"}
                    direction={
                      sortBy.sort_by === "date" ? sortBy.order : "desc"
                    }
                    onClick={() => handleSort("date")}
                    hideSortIcon={false} // <-- keep icon visible even when inactive
                    sx={{
                      color: "white !important",
                      cursor: "pointer",
                      "& .MuiTableSortLabel-icon": {
                        opacity: 1, // <-- don't fade when inactive
                        color: "white !important",
                      },
                    }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "white", whiteSpace: "nowrap" }}>
                  Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                if (!hasValidFilters) {
                  return (
                    <TableRow>
                      <TableCell colSpan={24} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  );
                }
                return logDetail?.length > 0 ? (
                  logDetail.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell align="center">{item.user_type}</TableCell>
                      <TableCell align="center">{item.emp_code}</TableCell>
                      <TableCell align="center"><ClickableTableLink path="/admin/userlist" displayValue={item.name} searchValue={item.emp_code} date={item.user_created_at ? item.user_created_at.substring(0, 4) : null} /></TableCell>
                      <TableCell align="center">{item.role_name}</TableCell>
                      <TableCell align="center">{item.email}</TableCell>
                      <TableCell align="center">{item.phone}</TableCell>
                      <TableCell align="center">{item?.last_login_time ? new Date(item.last_login_time).toLocaleDateString("en-GB") : "N/A"}</TableCell>
                      <TableCell align="center">{item?.last_login_time ? new Date(item.last_login_time).toLocaleString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      }) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : status === 'pending' ? (
                  <TableBodySkeleton cols={9} rows={10} />
                ) : (
                  <TableRow>
                    <TableCell colSpan={24} align="center">
                      No logs found
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={hasValidFilters ? counts : 0}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={hasValidFilters ? [10, 20, 50, 100, counts || 0] : [10, 20, 50, 100]}
        />
      </Box>
    </MainLayout>
  );
};

export default LogDetails;
